import classNames from "classnames";
import { Immutable } from "immer";
import React, { useEffect, useState } from "react";
import { t } from "ttag";

import { useAppSelector } from "../../../store/reducers";
import { GridFilter } from "../GridFilter";
import { GridSort } from "../GridSort";
import { GridFilterConfig } from "../filters";
import { GridVariant } from "../models";
import { listNonEmptyFilters, selectProducts } from "../selectors";
import { GridSorterConfig } from "../sorters";

import styles from "./index.module.scss";

const SideNav = React.memo(
    ({
        gridVariant: _gridVariant,
        filters,
        sorters,
        isMobile,
    }: Immutable<{
        gridVariant: GridVariant;
        filters: readonly GridFilterConfig[];
        /**
         * The sorters to render above the filters, if this grid puts its sort
         * here. Absent leaves the sort where it has always been, in `TopNav`.
         */
        sorters?: readonly GridSorterConfig[];
        isMobile: boolean;
    }>): React.ReactNode => {
        const products = useAppSelector(selectProducts);
        const [showFilters, setShowFilters] = useState(!isMobile);

        useEffect(() => {
            setShowFilters(!isMobile);
        }, [isMobile]);

        const toggleFilterView = (event: React.MouseEvent<HTMLDivElement>) => {
            event.preventDefault();
            setShowFilters(!showFilters);
        };

        const renderTitle = () => {
            const titleText = t`Filters`;
            const filterTitleToggleClass = classNames({
                [styles.title]: true,
                [styles.open]: showFilters,
                [styles.close]: !showFilters,
            });

            if (isMobile) {
                return (
                    <div
                        className={filterTitleToggleClass}
                        onClick={toggleFilterView}
                    >
                        {titleText}
                    </div>
                );
            }

            return <h2 className={styles.title}>{titleText}</h2>;
        };

        // Narrowed here rather than by the caller: this is the component
        // that draws the accordions, and every embedder -- the desktop
        // column and the mobile drawer alike -- should hide the same ones.
        // A hidden filter still filters, and still shows its removal chip
        // and answers Clear All; only its accordion is not drawn.
        // Narrowed on the data as well as the config: a filter whose options
        // are all derived from products has none to draw while a fetch is in
        // flight, and none at all in a category carrying none of the
        // attribute. `GridFilter` refuses to draw those; this column has to
        // answer the same question, or it reserves its track and heads it
        // with `Filters` over nothing. See `listNonEmptyFilters`.
        const shownFilters = listNonEmptyFilters(
            filters.filter((config) => config.showInSidebar),
            products,
        );

        // Nothing to draw is not the same as an empty column: `.container`
        // reserves a 200px flex track, so a grid whose filters are all hidden
        // -- every one of them driven by pills, which is what the field was
        // added for -- and whose sort stayed in `TopNav` would push the tiles
        // over to make room for a blank gutter.
        if (!sorters?.length && shownFilters.length <= 0) {
            return null;
        }

        return (
            <div className={styles.container}>
                {/* Above the Filters heading, not inside it: sorting is not a
                    filter, and the design reads as two sections in one
                    column. */}
                {sorters && <GridSort sorters={sorters} />}
                {/* A grid can now hide every filter -- all of them driven by
                    pills is the case the field was added for -- and a heading
                    labelling nothing, or a toggle opening onto nothing, is
                    worse than no section. */}
                {shownFilters.length > 0 && renderTitle()}
                {showFilters && shownFilters.length > 0 && (
                    <div>
                        {shownFilters.map((config) => (
                            <GridFilter
                                key={config.filterID}
                                config={config}
                                showSelectedList={false}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    },
);

SideNav.displayName = "SideNav";

export { SideNav };
